"use client";

import { c as _c } from "react/compiler-runtime";
import { FeaturedTemplateCard } from "@/app/(v2)/dashboard/_components/FeaturedTemplateCard";
import { PixelArrow } from "@/components/ui/pixel-arrow";
import { Skeleton } from "@/components/ui/skeleton";
import { SkeletonReveal } from "@/components/ui/skeleton-reveal";
import { cn } from "@/lib/utils";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useTemplatesStore, type Template } from "@/stores/templatesStore";
import { useEffect, useMemo, useRef, type RefObject } from "react";
const TEMPLATE_CARDS_INTERACTION_EVENT = "b3os-template-cards-interaction";
const MOBILE_MARQUEE_QUERY = "(max-width: 1023px)";
const MARQUEE_SPEED_PX_PER_SECOND = 18;
const TEMPLATE_CARDS_SKELETON_COUNT = 6;
const TEMPLATE_CARD_WIDTH_CLASS = "w-[calc(100vw-32px)] sm:w-[300px]";
const TEMPLATE_CARDS_VIEWPORT_CLASS = "scrollbar-hide mx-auto max-w-[1400px] px-4 sm:px-5 lg:max-w-[1422px] lg:px-0";
function notifyTemplateCardsInteraction() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(TEMPLATE_CARDS_INTERACTION_EVENT));
}
export function TemplateCardsList(t0) {
  const $ = _c(22);
  const {
    scrollRef
  } = t0;
  const templates = useTemplatesStore(_temp);
  const loading = useTemplatesStore(_temp2);
  const initialized = useTemplatesStore(_temp3);
  const ensureInitialized = useTemplatesStore(_temp4);
  const connectorTypes = useConnectorsStore(_temp5);
  const loadConnectorTypes = useConnectorsStore(_temp6);
  const marqueeStoppedRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== ensureInitialized) {
    t1 = () => {
      ensureInitialized();
    };
    t2 = [ensureInitialized];
    $[0] = ensureInitialized;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== connectorTypes.length || $[4] !== loadConnectorTypes) {
    t3 = () => {
      if (connectorTypes.length === 0) {
        loadConnectorTypes();
      }
    };
    t4 = [connectorTypes.length, loadConnectorTypes];
    $[3] = connectorTypes.length;
    $[4] = loadConnectorTypes;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== templates) {
    t6 = templates.filter(_temp7).sort(_temp8);
    $[7] = templates;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  t5 = t6;
  const promoted = t5;
  let t7;
  if ($[9] !== promoted.length || $[10] !== scrollRef.current) {
    t7 = () => {
      const el = scrollRef.current;
      if (!el || promoted.length < 2) {
        return;
      }
      const mobileQuery = window.matchMedia(MOBILE_MARQUEE_QUERY);
      const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      let rafId;
      rafId = null;
      let lastTime;
      lastTime = 0;
      const shouldRun = () => mobileQuery.matches && !reducedMotionQuery.matches && !marqueeStoppedRef.current && el.scrollWidth > el.clientWidth;
      const cancelMarquee = () => {
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
          rafId = null;
        }
        lastTime = 0;
      };
      const stopForPageView = () => {
        marqueeStoppedRef.current = true;
        cancelMarquee();
      };
      const tick = time => {
        if (!shouldRun()) {
          cancelMarquee();
          return;
        }
        if (lastTime === 0) {
          lastTime = time;
        }
        const deltaMs = Math.min(time - lastTime, 64);
        lastTime = time;
        const maxScroll = el.scrollWidth - el.clientWidth;
        el.scrollLeft = el.scrollLeft + MARQUEE_SPEED_PX_PER_SECOND * deltaMs / 1000;
        if (maxScroll > 0 && el.scrollLeft >= maxScroll - 1) {
          el.scrollLeft = 0;
        }
        rafId = window.requestAnimationFrame(tick);
      };
      const startMarquee = () => {
        if (rafId === null && shouldRun()) {
          rafId = window.requestAnimationFrame(tick);
        }
      };
      const restartIfAllowed = () => {
        cancelMarquee();
        startMarquee();
      };
      const interactionEvents = ["pointerdown", "touchstart", "wheel", "keydown", "focusin"];
      interactionEvents.forEach(eventName => el.addEventListener(eventName, stopForPageView, {
        passive: true
      }));
      window.addEventListener(TEMPLATE_CARDS_INTERACTION_EVENT, stopForPageView);
      mobileQuery.addEventListener("change", restartIfAllowed);
      reducedMotionQuery.addEventListener("change", restartIfAllowed);
      startMarquee();
      return () => {
        cancelMarquee();
        interactionEvents.forEach(eventName_0 => el.removeEventListener(eventName_0, stopForPageView));
        window.removeEventListener(TEMPLATE_CARDS_INTERACTION_EVENT, stopForPageView);
        mobileQuery.removeEventListener("change", restartIfAllowed);
        reducedMotionQuery.removeEventListener("change", restartIfAllowed);
      };
    };
    $[9] = promoted.length;
    $[10] = scrollRef.current;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== promoted.length || $[13] !== scrollRef) {
    t8 = [promoted.length, scrollRef];
    $[12] = promoted.length;
    $[13] = scrollRef;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t7, t8);
  const isInitialLoading = promoted.length === 0 && (!initialized || loading);
  if (isInitialLoading || promoted.length > 0) {
    let t9;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <TemplateCardsSkeleton />;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] !== promoted || $[17] !== scrollRef) {
      t10 = <TemplateCardsContent promoted={promoted} scrollRef={scrollRef} />;
      $[16] = promoted;
      $[17] = scrollRef;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    let t11;
    if ($[19] !== isInitialLoading || $[20] !== t10) {
      t11 = <SkeletonReveal loading={isInitialLoading} skeleton={t9}>{t10}</SkeletonReveal>;
      $[19] = isInitialLoading;
      $[20] = t10;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    return t11;
  }
  if (promoted.length === 0) {
    return null;
  }
  return null;
}
function _temp8(a, b) {
  return (a.promotedOrder ?? 0) - (b.promotedOrder ?? 0);
}
function _temp7(t) {
  return t.isPromoted;
}
function _temp6(state_4) {
  return state_4.loadConnectorTypes;
}
function _temp5(state_3) {
  return state_3.connectorTypes;
}
function _temp4(state_2) {
  return state_2._ensureInitialized;
}
function _temp3(state_1) {
  return state_1._initialized;
}
function _temp2(state_0) {
  return state_0.loading;
}
function _temp(state) {
  return state.templates;
}
function TemplateCardsContent(t0) {
  const $ = _c(8);
  const {
    promoted,
    scrollRef
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(TEMPLATE_CARDS_VIEWPORT_CLASS, "overflow-x-auto");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== promoted) {
    t2 = promoted.map(_temp9);
    $[1] = promoted;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="flex gap-4 pb-4 sm:gap-5">{t2}</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== scrollRef || $[6] !== t3) {
    t4 = <div className="w-full overflow-hidden py-3"><div ref={scrollRef} className={t1}>{t3}</div></div>;
    $[5] = scrollRef;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp9(template) {
  return <FeaturedTemplateCard key={template.id} template={template} className={TEMPLATE_CARD_WIDTH_CLASS} />;
}
function TemplateCardsSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn(TEMPLATE_CARDS_VIEWPORT_CLASS, "overflow-hidden");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-full overflow-hidden py-3" aria-label="Loading template cards"><div className={t0} aria-hidden={true}><div className="flex gap-4 pb-4 sm:gap-5">{Array.from({
            length: TEMPLATE_CARDS_SKELETON_COUNT
          }).map(_temp10)}</div></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp10(_, i) {
  return <TemplateCardSkeleton key={i} />;
}
function TemplateCardSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn("border-ds-border-default bg-ds-surface-card rounded-ds-md shadow-ds-soft block shrink-0 border", TEMPLATE_CARD_WIDTH_CLASS);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="bg-ds-surface-sunken border-ds-border rounded-ds-xs relative flex h-[105px] items-center justify-center overflow-hidden border"><div className="relative flex items-center gap-4"><span aria-hidden={true} className="border-ds-neutral-200 absolute left-[15px] right-[15px] top-1/2 -translate-y-1/2 border-t border-dashed" />{[0, 1, 2].map(_temp11)}</div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="rounded-ds-2xs h-[22px] w-[72%] max-w-[220px]" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={t0}><div className="flex flex-col gap-5 px-2 pb-5 pt-2">{t1}<div className="flex flex-col items-start gap-1 px-3.5">{t2}<div className="w-full space-y-1"><Skeleton className="rounded-ds-2xs h-4 w-full" /><Skeleton className="rounded-ds-2xs h-4 w-[78%]" /></div></div></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp11(index) {
  return <Skeleton key={index} className="rounded-ds-sm relative h-[56px] w-[56px]" />;
}
export function TemplateScrollButtons(t0) {
  const $ = _c(27);
  const {
    scrollRef,
    size: t1
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== scrollRef.current) {
    t2 = direction => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      notifyTemplateCardsInteraction();
      const distance = Math.min(Math.max(el.clientWidth * 0.85, 280), 420);
      el.scrollBy({
        left: direction === "left" ? -distance : distance,
        behavior: "smooth"
      });
    };
    $[0] = scrollRef.current;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scroll = t2;
  const compact = size === "compact";
  let t3;
  if ($[2] !== scroll) {
    t3 = () => scroll("left");
    $[2] = scroll;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = compact ? "h-10 w-[86px]" : "h-[47px] w-[104px]";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("border-ds-neutral-200 hover:bg-ds-surface-hover group flex items-center justify-center border bg-white transition-colors", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = compact ? "scale-[0.85]" : undefined;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <PixelArrow direction="left" />;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t6) {
    t8 = <span className={t6}>{t7}</span>;
    $[7] = t6;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t3 || $[10] !== t5 || $[11] !== t8) {
    t9 = <button onClick={t3} className={t5} aria-label="Scroll left">{t8}</button>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== scroll) {
    t10 = () => scroll("right");
    $[13] = scroll;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = compact ? "h-10 w-[86px]" : "h-[47px] w-[104px]";
  let t12;
  if ($[15] !== t11) {
    t12 = cn("border-ds-neutral-200 hover:bg-ds-surface-hover group -ml-px flex items-center justify-center border bg-white transition-colors", t11);
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const t13 = compact ? "scale-[0.85]" : undefined;
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <PixelArrow direction="right" />;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t13) {
    t15 = <span className={t13}>{t14}</span>;
    $[18] = t13;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t15) {
    t16 = <button onClick={t10} className={t12} aria-label="Scroll right">{t15}</button>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t16 || $[25] !== t9) {
    t17 = <div className="flex">{t9}{t16}</div>;
    $[24] = t16;
    $[25] = t9;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  return t17;
}